import React, { useState, useEffect } from 'react';
import './TradingConfigManager.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

const TradingConfigManager = () => {
  const [config, setConfig] = useState({
    min_trade_amount: 0.005
  });

  const [editValues, setEditValues] = useState({
    min_trade_amount: 0.005
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCurrentConfig();
  }, []);

  useEffect(() => {
    // Track if values have changed
    const changed = editValues.min_trade_amount !== config.min_trade_amount;
    setHasChanges(changed);
  }, [editValues, config]);

  const getAuthHeaders = () => {
    const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
    return {
      'Authorization': `Bearer ${tokens.access_token || ''}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchCurrentConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/trading-config`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data?.config) {
          setConfig(data.data.config);
          setEditValues(data.data.config);
        }
      }
    } catch (error) {
      console.error('Failed to fetch trading config:', error);
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/trading-config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          min_trade_amount: parseFloat(editValues.min_trade_amount)
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data?.config) {
          setConfig(data.data.config);
          setEditValues(data.data.config);
        }
        setMessage('Trading configuration saved successfully');
        setMessageType('success');
      } else {
        const error = await response.json();
        setMessage(error.message || 'Failed to save configuration');
        setMessageType('error');
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const resetToDefaults = () => {
    setEditValues({ min_trade_amount: 0.005 });
  };

  const presetAmounts = [
    { label: '0.001 SOL', value: 0.001, desc: 'Micro trades' },
    { label: '0.005 SOL', value: 0.005, desc: 'Default' },
    { label: '0.01 SOL', value: 0.01, desc: 'Standard' },
    { label: '0.05 SOL', value: 0.05, desc: 'Premium' },
    { label: '0.1 SOL', value: 0.1, desc: 'High value' }
  ];

  return (
    <div className="trading-config-manager">
      <div className="config-header">
        <div className="header-title">
          <h3>💰 Minimum Trade Amount</h3>
          <p className="header-description">
            Configure the minimum SOL amount required for all trading operations including
            automated bots, manual trades, and quick buys.
          </p>
        </div>
        <div className="current-value-badge">
          <span className="badge-label">Current</span>
          <span className="badge-value">{config.min_trade_amount} SOL</span>
        </div>
      </div>

      {message && (
        <div className={`config-message ${messageType}`}>
          <span className="message-icon">
            {messageType === 'success' ? '✅' : '❌'}
          </span>
          {message}
        </div>
      )}

      <div className="config-content">
        {/* Quick Presets */}
        <div className="config-section">
          <label className="section-label">Quick Presets</label>
          <div className="preset-grid">
            {presetAmounts.map((preset) => (
              <button
                key={preset.value}
                className={`preset-button ${editValues.min_trade_amount === preset.value ? 'active' : ''}`}
                onClick={() => setEditValues({ ...editValues, min_trade_amount: preset.value })}
                disabled={loading}
              >
                <span className="preset-amount">{preset.label}</span>
                <span className="preset-desc">{preset.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Amount Input */}
        <div className="config-section">
          <label className="section-label">Custom Amount</label>
          <div className="custom-input-group">
            <div className="input-wrapper">
              <input
                type="number"
                className="config-input"
                value={editValues.min_trade_amount}
                onChange={(e) => setEditValues({
                  ...editValues,
                  min_trade_amount: parseFloat(e.target.value) || 0
                })}
                min="0.0001"
                max="10"
                step="0.001"
                disabled={loading}
              />
              <span className="input-suffix">SOL</span>
            </div>
            <div className="input-range">
              <input
                type="range"
                min="0.001"
                max="1"
                step="0.001"
                value={editValues.min_trade_amount}
                onChange={(e) => setEditValues({
                  ...editValues,
                  min_trade_amount: parseFloat(e.target.value)
                })}
                className="range-slider"
                disabled={loading}
              />
              <div className="range-labels">
                <span>0.001</span>
                <span>0.25</span>
                <span>0.5</span>
                <span>0.75</span>
                <span>1.0</span>
              </div>
            </div>
          </div>
        </div>

        {/* Impact Info */}
        <div className="config-section impact-section">
          <label className="section-label">Impact</label>
          <div className="impact-grid">
            <div className="impact-card">
              <span className="impact-icon">🤖</span>
              <div className="impact-text">
                <strong>Automated Bots</strong>
                <span>Minimum initial buy & re-entry amount</span>
              </div>
            </div>
            <div className="impact-card">
              <span className="impact-icon">⚡</span>
              <div className="impact-text">
                <strong>Quick Buy</strong>
                <span>Minimum amount for discovery quick buys</span>
              </div>
            </div>
            <div className="impact-card">
              <span className="impact-icon">📊</span>
              <div className="impact-text">
                <strong>Manual Trading</strong>
                <span>Minimum buy order size</span>
              </div>
            </div>
            <div className="impact-card">
              <span className="impact-icon">🔄</span>
              <div className="impact-text">
                <strong>Re-Entry</strong>
                <span>Minimum balance for stop-loss re-entry</span>
              </div>
            </div>
          </div>
        </div>

        {/* Warning */}
        {editValues.min_trade_amount < 0.005 && (
          <div className="config-warning">
            <span className="warning-icon">⚠️</span>
            <div className="warning-text">
              <strong>Low Minimum Warning</strong>
              <p>
                Setting the minimum below 0.005 SOL may result in failed transactions
                due to insufficient funds for network fees (priority fees + rent).
              </p>
            </div>
          </div>
        )}

        {editValues.min_trade_amount > 0.5 && (
          <div className="config-warning info">
            <span className="warning-icon">ℹ️</span>
            <div className="warning-text">
              <strong>High Minimum Notice</strong>
              <p>
                A high minimum trade amount may prevent users with smaller balances
                from participating in trading.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="config-actions">
        <button
          className="action-btn reset-btn"
          onClick={resetToDefaults}
          disabled={loading}
        >
          Reset to Default
        </button>
        <button
          className="action-btn save-btn"
          onClick={saveConfig}
          disabled={loading || !hasChanges}
        >
          {loading ? (
            <>
              <span className="btn-spinner"></span>
              Saving...
            </>
          ) : (
            <>
              💾 Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TradingConfigManager;
